export const CHALLENGE_META_REF = "tapOrSkip_challenges_meta";
export const CHALLENGE_FRIEND_REF = "tapOrSkip_friend_answers";
/** מפתח legacy לפני הפרדה לפי Google UID — נקרא פעם אחת להעברה */
export const MY_CHALLENGES_KEY = "dilemma_my_challenge_ids";
const ANON_SLOT = "__anon";

function storageKeyForUid(googleUid) {
  const u = googleUid != null && String(googleUid).trim() ? String(googleUid).trim() : "";
  return u ? `${MY_CHALLENGES_KEY}:${u}` : `${MY_CHALLENGES_KEY}:${ANON_SLOT}`;
}

function parseChallengeIdList(raw) {
  if (!raw) return [];
  try {
    let arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .map((x) => {
        if (typeof x === "string") return {id: x, at: 0, role: "creator"};
        if (!x || !x.id) return null;
        return {id: x.id, at: typeof x.at === "number" ? x.at : 0, role: x.role === "friend" ? "friend" : "creator"};
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * טוען את רשימת מזהי האתגרים של המשתמש הנוכחי בלבד (לפי Google UID).
 * @param {string | null | undefined} googleUid — מ־auth.currentUser?.uid; בלי התחברות — מגירת אנונימי נפרדת
 */
export function loadMyChallengeIds(googleUid) {
  try {
    const key = storageKeyForUid(googleUid);
    let raw = localStorage.getItem(key);
    if (!raw) {
      const legacy = localStorage.getItem(MY_CHALLENGES_KEY);
      if (legacy) {
        localStorage.setItem(key, legacy);
        localStorage.removeItem(MY_CHALLENGES_KEY);
        raw = legacy;
      }
    }
    return parseChallengeIdList(raw);
  } catch {
    return [];
  }
}

/**
 * @param {string} id
 * @param {"creator" | "friend"} role
 * @param {string | null | undefined} googleUid — מ־auth.currentUser?.uid
 */
export function addMyChallengeId(id, role = "creator", googleUid) {
  try {
    if (!id) return;
    const key = storageKeyForUid(googleUid);
    const arr = parseChallengeIdList(localStorage.getItem(key));
    const next = arr.filter((x) => x.id !== id);
    next.unshift({id, at: Date.now(), role: role === "friend" ? "friend" : "creator"});
    localStorage.setItem(key, JSON.stringify(next.slice(0, 100)));
  } catch {
    /* ignore */
  }
}

function mergeChallengeIdLists(a, b) {
  const byId = new Map();
  for (const x of b) {
    if (x?.id) byId.set(x.id, x);
  }
  for (const x of a) {
    if (!x?.id) continue;
    const o = byId.get(x.id);
    if (!o) {
      byId.set(x.id, x);
    } else {
      byId.set(x.id, {
        id: x.id,
        at: Math.max(o.at, x.at),
        role: o.role === "creator" || x.role === "creator" ? "creator" : "friend",
      });
    }
  }
  return [...byId.values()].sort((p, q) => q.at - p.at).slice(0, 100);
}

/** אחרי התחברות Google — מעביר אתגרים ממצב אנונימי לאותו דפדפן לרשימה של ה-UID */
export function mergeAnonMyChallengesIntoUid(googleUid) {
  if (!googleUid || !String(googleUid).trim()) return;
  try {
    const anonKey = storageKeyForUid(null);
    const uidKey = storageKeyForUid(googleUid);
    const anonList = parseChallengeIdList(localStorage.getItem(anonKey));
    if (!anonList.length) return;
    const uidList = parseChallengeIdList(localStorage.getItem(uidKey));
    const merged = mergeChallengeIdLists(uidList, anonList);
    localStorage.setItem(uidKey, JSON.stringify(merged));
    localStorage.removeItem(anonKey);
  } catch {
    /* ignore */
  }
}

export function avgMsFromRounds(rounds) {
  if (!rounds?.length) return 0;
  return Math.round(rounds.reduce((s, r) => s + (r.msUsed || 0), 0) / rounds.length);
}

/**
 * השוואת ריצות: התאמה נספרת רק כששני הצדדים ענו (A/B).
 * אם לפחות אחד לא ענה בזמן — נכנס ל־missed (לא נספר בהתאמה).
 */
export function compareChallengeRuns(creatorRounds, friendRounds, dilemmas) {
  const n = dilemmas?.length || 0;
  let matches = 0;
  let answeredTotal = 0;
  const diffs = [];
  const sames = [];
  const missed = [];
  for (let i = 0; i < n; i++) {
    const cRound = creatorRounds?.find((r) => r.i === i);
    const fRound = friendRounds?.find((r) => r.i === i);
    const a = cRound?.choice ?? null;
    const b = fRound?.choice ?? null;
    const d = dilemmas[i] || {};
    const labelA = a === "A" ? d.a : a === "B" ? d.b : null;
    const labelB = b === "A" ? d.a : b === "B" ? d.b : null;
    if (a !== null && b !== null) {
      answeredTotal++;
      if (a === b) {
        matches++;
        sames.push({i, optA: d.a || "—", optB: d.b || "—", choice: labelA});
      } else {
        diffs.push({i, optA: d.a || "—", optB: d.b || "—", creatorChoice: labelA, friendChoice: labelB});
      }
    } else {
      missed.push({
        i,
        optA: d.a || "—",
        optB: d.b || "—",
        creatorChoice: labelA,
        friendChoice: labelB,
      });
    }
  }
  return {matches, diffs, sames, missed, total: n, answeredTotal};
}

export function buildChallengeLink(id) {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/dilema?challenge=${encodeURIComponent(id)}`;
}

export const AVATAR_EMOJIS = ["🦁", "🐻", "🦊", "🐼", "🦄", "🐸", "🐨", "🐯", "⭐", "🌟", "🔥", "💎", "🎮", "🎯", "🚀"];
